import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

export interface DraftEvidenceItem {
  url: string;
  fileName: string;
  uploadedAt: string;
}

export type TaskEvidenceDraft = {
  taskId: string;
  organizationId?: string;
  assetEvidence: Record<
    string,
    Record<string, DraftEvidenceItem>
  >;
  updatedAt?: any;
};

const COLLECTION = 'task_evidence_drafts';

export const taskEvidenceDraftService = {
  async getDraft(taskId: string): Promise<{ success: boolean; data?: TaskEvidenceDraft }> {
    try {
      const ref = doc(db, COLLECTION, taskId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        return { success: true, data: { ...snap.data(), taskId } as TaskEvidenceDraft };
      }
      return { success: true, data: undefined };
    } catch (error: any) {
      console.error('[taskEvidenceDraftService] getDraft error:', error);
      return { success: false };
    }
  },

  async saveEvidence(
    taskId: string,
    assetId: string,
    evidenceKey: string,
    item: DraftEvidenceItem,
    organizationId?: string
  ): Promise<{ success: boolean }> {
    try {
      const ref = doc(db, COLLECTION, taskId);
      const snap = await getDoc(ref);
      const existing = snap.exists() ? (snap.data() as TaskEvidenceDraft) : null;
      const assetEvidence = existing?.assetEvidence || {};
      const assetData = assetEvidence[assetId] || {};
      const updated = {
        ...assetData,
        [evidenceKey]: item,
      };
      const nextAssetEvidence = {
        ...assetEvidence,
        [assetId]: updated,
      };
      await setDoc(ref, {
        taskId,
        organizationId: organizationId || existing?.organizationId,
        assetEvidence: nextAssetEvidence,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('[taskEvidenceDraftService] saveEvidence error:', error);
      return { success: false };
    }
  },

  async removeEvidence(
    taskId: string,
    assetId: string,
    evidenceKey: string
  ): Promise<{ success: boolean }> {
    try {
      const ref = doc(db, COLLECTION, taskId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return { success: true };
      const data = snap.data() as TaskEvidenceDraft;
      const assetEvidence = data.assetEvidence || {};
      const assetData = assetEvidence[assetId] || {};
      const { [evidenceKey]: _removed, ...rest } = assetData;
      const nextAssetEvidence = { ...assetEvidence };
      if (Object.keys(rest).length > 0) {
        nextAssetEvidence[assetId] = rest;
      } else {
        delete nextAssetEvidence[assetId];
      }
      await setDoc(ref, {
        taskId: data.taskId,
        organizationId: data.organizationId,
        assetEvidence: nextAssetEvidence,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      console.error('[taskEvidenceDraftService] removeEvidence error:', error);
      return { success: false };
    }
  },

  /** Clear draft after task is successfully completed */
  async clearDraft(taskId: string): Promise<{ success: boolean }> {
    try {
      const ref = doc(db, COLLECTION, taskId);
      await setDoc(ref, { taskId, assetEvidence: {}, updatedAt: serverTimestamp() });
      return { success: true };
    } catch (error: any) {
      console.error('[taskEvidenceDraftService] clearDraft error:', error);
      return { success: false };
    }
  },
};
